import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Box, Tooltip } from '@mui/material';
import { AutosuggestDropdown, Suggestion, useFormContext } from 'framework/form';
import { ListParams } from 'framework/list';
import { statusConfig, StatusType } from '../../../availabilityUser/constants';
import { AvailabilityUserStatus } from '../../../availabilityUser/types';
import useAvailabilityUserService from '../../../availabilityUser/useAvailabilityUserService';

const MIN_QUERY_LENGTH = 3;
const RECIPIENT_STATUS_WARNING =
  'This interruption is unlikely to be accepted while the person is offline or not working.';

const OPTION_SX = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  minHeight: 24,
  minWidth: 0,
} as const;

const STATUS_ICON_SX = {
  width: 16,
  height: 16,
  flexShrink: 0,
} as const;

const OPTION_TEXT_SX = {
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
} as const;

const END_ADORNMENT_SX = {
  alignItems: 'center',
  display: 'inline-flex',
  gap: 0.75,
} as const;

const isStatusType = (status: unknown): status is StatusType =>
  Object.values(StatusType).includes(status as StatusType);

const normalizeStatus = (status: unknown): StatusType => (isStatusType(status) ? status : StatusType.OFFLINE);

const getSuggestionRecipientState = (suggestion: Suggestion) => {
  const { recipient } = suggestion;

  return recipient && typeof recipient === 'object' && 'state' in recipient ? recipient.state : undefined;
};

const RecipientSelectField = () => {
  const { data } = useFormContext();
  const { searchAvailabilityUsers } = useAvailabilityUserService();
  const selectedRecipient = (data?.recipient || null) as AvailabilityUserStatus | null;
  const selectedStatus = selectedRecipient ? normalizeStatus(selectedRecipient.state) : null;

  const shouldWarnAboutRecipientStatus =
    selectedStatus === StatusType.OFFLINE || selectedStatus === StatusType.NON_WORKING_INTERRUPTIBLE;

  const searchRequest = (params: ListParams) => {
    const query = params.filter?.employeeName?.ct || '';

    return searchAvailabilityUsers(query);
  };

  const loadSuggestions = (searchValue: string) => {
    if ((searchValue || '').trim().length < MIN_QUERY_LENGTH) {
      return Promise.resolve({ result: [], totalPages: 0 });
    }

    return searchAvailabilityUsers(searchValue);
  };

  const mapResults = (recipient: AvailabilityUserStatus) => ({
    id: recipient.employeeId,
    text: recipient.employeeName,
    recipient,
  });

  const renderSuggestion = (suggestion: Suggestion) => {
    const status = normalizeStatus(getSuggestionRecipientState(suggestion));

    return (
      <Box sx={OPTION_SX}>
        <Box component="img" src={statusConfig[status].icon} alt="" aria-hidden="true" sx={STATUS_ICON_SX} />
        <Box component="span" sx={OPTION_TEXT_SX}>
          {suggestion.text}
        </Box>
      </Box>
    );
  };

  const endAdornment = selectedStatus ? (
    <Box sx={END_ADORNMENT_SX}>
      {shouldWarnAboutRecipientStatus ? (
        <Tooltip title={RECIPIENT_STATUS_WARNING}>
          <WarningAmberIcon color="warning" fontSize="small" />
        </Tooltip>
      ) : null}
      <Box component="img" src={statusConfig[selectedStatus].icon} alt="" aria-hidden="true" sx={STATUS_ICON_SX} />
    </Box>
  ) : null;

  return (
    <AutosuggestDropdown
      source="recipient"
      initialValue={selectedRecipient?.employeeName || ''}
      loadSuggestionsMethod={loadSuggestions}
      searchRequest={searchRequest}
      mapResults={mapResults}
      renderSuggestion={renderSuggestion}
      EndAdornment={endAdornment}
      noResultMessage="No users found. Try another query."
      label="Search employee by name"
    />
  );
};

export default RecipientSelectField;
